import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Calendar, User, ExternalLink, Linkedin, ArrowRight } from 'lucide-react';

// Animation variant
const fadeInUp = {
  initial: { opacity: 0, y: 40 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-100px" },
  transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] }
};

const blogsData = {
  'ai-and-future-of-marketing': {
    title: 'AI IS NOT REPLACEMENT. IT IS AMPLIFICATION.',
    author: 'SHASHANK JOSHI',
    authorLinkedin: 'https://in.linkedin.com/in/shashank-joshi-a05b4732',
    date: 'December 2024',
    linkedinUrl: 'https://www.linkedin.com/posts/shashank-joshi-a05b4732_a-couple-of-days-ago-mr-vinod-khosla-made-activity-7430815990327730176-N14-',
    image: '/3d-performance-marketing.png',
    content: [
      { type: 'paragraph', text: 'A couple of days ago, Mr. Vinod Khosla made a statement that predictably unsettled people — "IT Services Will Vanish By 2030"' },
      { type: 'paragraph', text: 'Strip away the headline drama, and the core message is difficult to dispute. AI isn\'t nibbling at the edges anymore rather moving straight into the very territory once considered automation-proof.' },
      { type: 'paragraph', text: 'At Zenith Marketing India, this shift stopped being theoretical years ago.' },
      { type: 'paragraph', text: 'We gradually replaced large parts of our traditional content workflow with AI systems. Not out of fascination, but necessity.' },
      { type: 'highlight', text: 'Faster iterations. Fewer bottlenecks. Greater creative flexibility.' },
      { type: 'paragraph', text: 'The outcome was improvement. Output quality rose. Turnaround times collapsed. Experimentation became cheap.' },
      { type: 'paragraph', text: 'This is where the conversation often derails.' },
      { type: 'heading', text: 'AI IS NOT HUMANS VS MACHINES. IT NEVER HAS BEEN.' },
      { type: 'paragraph', text: 'Progress has always followed the same formula: man + tools.' },
      { type: 'list', items: [
        'The plough didn\'t eliminate farmers.',
        'Computers didn\'t eliminate offices.',
        'The .com revolution didn\'t eliminate commerce.'
      ]},
      { type: 'paragraph', text: 'Each wave initially triggered panic, then productivity, then entirely new industries.' },
      { type: 'quote', text: 'Major technological shifts create more opportunities than they erase.' },
      { type: 'paragraph', text: 'We are just watching that cycle repeat.' },
      { type: 'paragraph', text: 'As with all others, my area of work - Marketing, is being rewritten in real time. Content creation, visual production, campaign testing, personalisation — everything is compressing.' },
      { type: 'paragraph', text: 'Tools like Emergent are redefining how websites and applications are built. Midjourney-style systems are reshaping visual ideation. What once required studios, equipment, and elongated timelines now often begins and ends inside an AI workflow.' },
      { type: 'paragraph', text: 'Even product photography, long treated as a logistical ritual, has quietly become optional in many cases. AI-driven visual generation offers control, adaptability, and speed that physical shoots struggle to match.' },
      { type: 'heading', text: 'BUT NONE OF THIS ELIMINATES THE HUMAN LAYER.' },
      { type: 'paragraph', text: 'Strategy, taste, judgment, cultural intuition — these are not disappearing. If anything, they are becoming more valuable. When production friction drops, thinking becomes the differentiator.' },
      { type: 'paragraph', text: 'That is the real shift.' },
      { type: 'highlight', text: 'AI is not the story of replacement. It is the story of amplification.' },
      { type: 'paragraph', text: 'And like computers before it, resistance will eventually look less like caution and more like nostalgia.' }
    ]
  },
  'social-media-metrics-2026': {
    title: 'THE "LIKE" BUTTON IS OFFICIALLY A VANITY METRIC',
    author: 'SHASHANK JOSHI',
    authorLinkedin: 'https://in.linkedin.com/in/shashank-joshi-a05b4732',
    date: 'November 2024',
    linkedinUrl: 'https://www.linkedin.com/posts/shashank-joshi-a05b4732_the-like-button-is-officially-a-vanity-activity-7413797975468044288-7wYw',
    image: '/3d-social-media-mgmt.png',
    content: [
      { type: 'highlight', text: 'The "Like" button is officially a vanity metric.' },
      { type: 'paragraph', text: 'If you\'re still reporting reach and likes in 2026, you\'re measuring the past.' },
      { type: 'paragraph', text: 'The algorithm hasn\'t broken — it has matured.' },
      { type: 'paragraph', text: 'We\'ve moved from the Feed Era to the Chat Era.' },
      { type: 'paragraph', text: 'At Zenith Marketing, we\'re seeing a clear shift in what actually drives growth:' },
      { type: 'heading', text: '1. THE SILENT SHARE ECONOMY' },
      { type: 'paragraph', text: 'The most valuable action today is invisible. When someone DMs your post to a friend, that\'s real signal — high intent, high relevance. A private share now outweighs a public like.' },
      { type: 'quote', text: 'Stop optimising for applause. Start optimising for the Send button.' },
      { type: 'heading', text: '2. RETENTION IS THE NEW REACH' },
      { type: 'paragraph', text: 'Instagram is no longer a distribution platform. It\'s a retention engine. If your Reel loses attention in the first 3 seconds, you disappear. If viewers stay till the end, you earn momentum.' },
      { type: 'quote', text: 'Raw beats polished. If it feels like an ad, it scrolls like one.' },
      { type: 'heading', text: '3. KEYWORDS > HASHTAGS' },
      { type: 'paragraph', text: 'Hashtag stuffing is obsolete. The algorithm now reads your on-screen text and listens to your audio.' },
      { type: 'quote', text: 'Write captions like SEO. Be clear before being clever.' },
      { type: 'heading', text: 'THE BOTTOM LINE' },
      { type: 'paragraph', text: 'You don\'t need a massive following anymore. You need content valuable enough to be saved, sent, and returned to.' },
      { type: 'highlight', text: 'Stop trying to be seen by everyone. Start being useful to the right few.' }
    ]
  }
};

const BlogDetailPage = () => {
  const { slug } = useParams();
  const blog = blogsData[slug];

  if (!blog) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="text-center">
          <h1 className="font-outfit text-4xl font-bold text-gray-900 mb-4 uppercase">BLOG NOT FOUND</h1>
          <Link to="/blog" className="text-brand-blue hover:text-cyan-500 transition-colors uppercase">
            ← BACK TO ALL BLOGS
          </Link>
        </div>
      </div>
    );
  }

  const renderContent = (item, index) => {
    switch (item.type) {
      case 'paragraph':
        return <p key={index} className="text-lg text-gray-600 leading-relaxed mb-6">{item.text}</p>;
      case 'heading':
        return <h2 key={index} className="font-outfit text-2xl md:text-3xl font-bold text-gray-900 mt-12 mb-6 uppercase">{item.text}</h2>;
      case 'highlight':
        return (
          <div key={index} className="bg-gradient-to-r from-brand-blue/5 to-cyan-400/5 border-l-4 border-brand-blue p-6 rounded-r-xl my-8">
            <p className="text-xl font-semibold text-gray-900">{item.text}</p>
          </div>
        );
      case 'quote':
        return (
          <blockquote key={index} className="border-l-4 border-brand-blue pl-6 py-2 my-8 italic text-lg text-gray-600">
            "{item.text}"
          </blockquote>
        );
      case 'list':
        return (
          <ul key={index} className="list-disc list-inside space-y-2 my-6 text-lg text-gray-600">
            {item.items.map((li, i) => <li key={i}>{li}</li>)}
          </ul>
        );
      default:
        return null;
    }
  };

  return (
    <div className="w-full pt-20">
      {/* Hero */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 relative overflow-hidden" data-testid="blog-detail-hero">
        <div className="absolute inset-0 -z-10">
          <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-gradient-to-bl from-brand-blue/20 via-cyan-500/10 to-transparent rounded-full blur-3xl" />
        </div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeInUp}>
            <Link to="/blog" className="inline-flex items-center text-white/70 hover:text-white mb-8 transition-colors uppercase">
              <ArrowLeft size={20} className="mr-2" />
              BACK TO ALL BLOGS
            </Link>
            
            <h1 className="font-outfit text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-8 leading-tight uppercase">
              {blog.title}
            </h1>
            
            <div className="flex flex-wrap items-center gap-6 text-white/70">
              <a 
                href={blog.authorLinkedin}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 hover:text-white transition-colors uppercase"
              >
                <User size={18} />
                {blog.author}
                <Linkedin size={16} className="text-brand-blue" />
              </a>
              <span className="flex items-center gap-2 uppercase">
                <Calendar size={18} />
                {blog.date}
              </span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 md:py-24 bg-gradient-to-b from-white to-gray-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.article {...fadeInUp} className="prose prose-lg max-w-none">
            {blog.content.map((item, index) => renderContent(item, index))}
          </motion.article>
          
          {/* Author & Share */}
          <motion.div {...fadeInUp} className="mt-16 pt-8 border-t border-gray-200">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-6">
              <div className="flex items-center gap-4">
                <img 
                  src="/founder-shashank-new.jpg"
                  alt="Shashank Joshi"
                  className="w-16 h-16 rounded-full object-cover border-2 border-brand-blue"
                />
                <div>
                  <a 
                    href={blog.authorLinkedin}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-semibold text-gray-900 hover:text-brand-blue transition-colors flex items-center gap-2 uppercase"
                  >
                    {blog.author}
                    <Linkedin size={16} className="text-blue-600" />
                  </a>
                  <p className="text-gray-500 uppercase">FOUNDER, ZENITH MARKETING INDIA</p>
                </div>
              </div>
              
              <a
                href={blog.linkedinUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-6 py-3 rounded-full bg-gradient-to-r from-blue-600 to-blue-700 text-white font-medium hover:from-blue-700 hover:to-blue-600 transition-all uppercase"
              >
                <Linkedin size={20} className="mr-2" />
                VIEW ORIGINAL POST
                <ExternalLink size={16} className="ml-2" />
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* More Blogs CTA */}
      <section className="py-16 bg-gradient-to-b from-gray-50 to-gray-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div {...fadeInUp}>
            <h3 className="font-outfit text-2xl font-bold text-gray-900 mb-4 uppercase">WANT MORE INSIGHTS?</h3>
            <Link
              to="/blog"
              className="inline-flex items-center text-brand-blue font-medium hover:text-cyan-500 transition-colors uppercase"
            >
              VIEW ALL BLOG POSTS
              <ArrowRight size={18} className="ml-2" />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default BlogDetailPage;
